'use client';

import React, { useState, useCallback } from 'react';
import {
  Package, Hash, Calendar, Layers, ChevronDown, Plus, CheckCircle2,
  RotateCcw, AlertTriangle, Trash2, Eye, ClipboardList, Boxes
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface InventoryItem {
  id: string;
  itemName: string;
  batchNumber: string;
  mfgDate: string;
  expiryDate: string;
  quantity: string;
  uom: string;
  rack: string;
  remarks: string;
  status: 'pending';
  addedAt: string;
}

interface ItemFormData {
  itemName: string;
  batchNumber: string;
  mfgDate: string;
  expiryDate: string;
  noExpiry: boolean;
  quantity: string;
  uom: string;
  rack: string;
  remarks: string;
}

const EMPTY_ITEM: ItemFormData = {
  itemName: '',
  batchNumber: '',
  mfgDate: '',
  expiryDate: '',
  noExpiry: false,
  quantity: '',
  uom: 'PCS',
  rack: '',
  remarks: '',
};

const MOCK_GRNS = [
  { id: 'GRN-20260612-001', invoice: 'INV-2026-00123', supplier: 'MedSupply India Pvt Ltd', date: '12 Jun 2026' },
  { id: 'GRN-20260612-002', invoice: 'INV-2026-00124', supplier: 'Surgiwear Ltd', date: '12 Jun 2026' },
  { id: 'GRN-20260611-087', invoice: 'INV-2026-00119', supplier: 'Indo Surgical Pvt Ltd', date: '11 Jun 2026' },
  { id: 'GRN-20260611-088', invoice: 'INV-2026-00120', supplier: 'MedSupply India Pvt Ltd', date: '11 Jun 2026' },
];

const UOM_OPTIONS = ['PCS', 'BOX', 'PACK', 'ROLL', 'KG', 'LTR', 'SET', 'PAIR'];

function generateItemId(): string {
  return `INV-${Date.now().toString().slice(-8)}`;
}

function shelfLifePercent(mfg: string, exp: string): number | null {
  if (!mfg || !exp) return null;
  const mfgD = new Date(mfg).getTime();
  const expD = new Date(exp).getTime();
  const now = Date.now();
  if (expD <= mfgD) return null;
  const total = expD - mfgD;
  const remaining = expD - now;
  return Math.max(0, Math.round((remaining / total) * 100));
}

export default function InventoryAddForm() {
  const [selectedGRN, setSelectedGRN] = useState<string>('');
  const [grnOpen, setGrnOpen] = useState(false);
  const [form, setForm] = useState<ItemFormData>(EMPTY_ITEM);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = useCallback((field: keyof ItemFormData) => (v: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: v }));
  }, []);

  const selectedGRNData = MOCK_GRNS.find((g) => g.id === selectedGRN);
  const isItemValid = form.itemName.trim() && form.quantity.trim() && selectedGRN;

  const shelfPct = shelfLifePercent(form.mfgDate, form.expiryDate);

  const handleAddItem = () => {
    if (!isItemValid) return;
    const newItem: InventoryItem = {
      id: generateItemId(),
      itemName: form.itemName,
      batchNumber: form.batchNumber || `AUTO-${Date.now().toString().slice(-6)}`,
      mfgDate: form.mfgDate,
      expiryDate: form.noExpiry ? 'No Expiry' : form.expiryDate,
      quantity: form.quantity,
      uom: form.uom,
      rack: form.rack,
      remarks: form.remarks,
      status: 'pending',
      addedAt: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
    setItems((prev) => [newItem, ...prev]);
    setForm(EMPTY_ITEM);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleSubmitForQC = () => {
    if (items.length === 0) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 900);
  };

  const handleReset = () => {
    setItems([]);
    setForm(EMPTY_ITEM);
    setSelectedGRN('');
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Items Submitted for QC</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {items.length} item{items.length !== 1 ? 's' : ''} under GRN <span className="font-mono text-primary">{selectedGRN}</span> are now pending QC review on the tablet.
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-left space-y-2">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{item.itemName}</p>
                  <p className="text-2xs text-muted-foreground">Batch: {item.batchNumber} · Qty: {item.quantity} {item.uom}</p>
                </div>
                <span className="badge-pending text-2xs font-bold px-2 py-0.5 rounded-full">PENDING QC</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 mx-auto px-5 py-2.5 rounded-lg bg-muted text-foreground text-sm font-semibold hover:bg-muted/70 transition-all"
          >
            <RotateCcw size={14} />
            Add More Items
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* GRN Selection */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <ClipboardList size={16} className="text-primary" />
            <h2 className="text-sm font-bold text-foreground">Step 1 — Select GRN</h2>
            {selectedGRN && <span className="ml-auto badge-available text-2xs font-bold px-2 py-0.5 rounded-full">SELECTED</span>}
          </div>
          <div className="p-5">
            <div className="relative">
              <button
                type="button"
                onClick={() => setGrnOpen((o) => !o)}
                className="w-full flex items-center justify-between bg-muted/40 border border-border rounded-lg px-4 py-3 text-sm text-foreground hover:border-primary/50 transition-all"
              >
                {selectedGRN ? (
                  <span className="flex items-center gap-2">
                    <span className="font-mono font-bold text-primary">{selectedGRN}</span>
                    <span className="text-muted-foreground text-xs">· {selectedGRNData?.supplier}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select a GRN for today…</span>
                )}
                <ChevronDown size={16} className={`text-muted-foreground transition-transform ${grnOpen ? 'rotate-180' : ''}`} />
              </button>
              {grnOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-20 overflow-hidden fade-in">
                  {MOCK_GRNS.map((grn) => (
                    <button
                      key={grn.id}
                      type="button"
                      onClick={() => { setSelectedGRN(grn.id); setGrnOpen(false); }}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-0 ${selectedGRN === grn.id ? 'bg-primary/8' : ''}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Package size={14} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono font-bold text-primary">{grn.id}</p>
                        <p className="text-2xs text-muted-foreground truncate">{grn.invoice} · {grn.supplier}</p>
                      </div>
                      <span className="text-2xs text-muted-foreground flex-shrink-0">{grn.date}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedGRNData && (
              <div className="mt-3 flex items-center gap-4 px-3 py-2 rounded-lg bg-primary/5 border border-primary/15">
                <div className="flex-1 min-w-0">
                  <p className="text-2xs text-muted-foreground">Supplier</p>
                  <p className="text-xs font-semibold text-foreground">{selectedGRNData.supplier}</p>
                </div>
                <div>
                  <p className="text-2xs text-muted-foreground">Invoice</p>
                  <p className="text-xs font-semibold text-foreground">{selectedGRNData.invoice}</p>
                </div>
                <div>
                  <p className="text-2xs text-muted-foreground">Date</p>
                  <p className="text-xs font-semibold text-foreground">{selectedGRNData.date}</p>
                </div>
                <StatusBadge status="quarantine" size="sm" />
              </div>
            )}
          </div>
        </div>

        {/* Item Entry Form */}
        <div className={`bg-card border border-border rounded-xl overflow-hidden transition-opacity ${!selectedGRN ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Boxes size={16} className="text-primary" />
            <h2 className="text-sm font-bold text-foreground">Step 2 — Add Item Details</h2>
          </div>
          <div className="p-5 space-y-5">

            {/* Row 1: Item Name + Batch */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Package size={12} /> Item Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.itemName}
                  onChange={(e) => setField('itemName')(e.target.value)}
                  placeholder="e.g. Non Woven Tape 75mm x 250cm"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Hash size={12} /> Batch Number
                </label>
                <input
                  type="text"
                  value={form.batchNumber}
                  onChange={(e) => setField('batchNumber')(e.target.value)}
                  placeholder="Auto-generated if left blank"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Row 2: Mfg Date + Expiry Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Calendar size={12} /> Mfg Date
                </label>
                <input
                  type="date"
                  value={form.mfgDate}
                  onChange={(e) => setField('mfgDate')(e.target.value)}
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Calendar size={12} /> Expiry Date
                  {!form.noExpiry && <span className="text-red-400">*</span>}
                </label>
                <input
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setField('expiryDate')(e.target.value)}
                  disabled={form.noExpiry}
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all disabled:opacity-40"
                />
                <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                  <input
                    type="checkbox"
                    checked={form.noExpiry}
                    onChange={(e) => setField('noExpiry')(e.target.checked)}
                    className="w-3.5 h-3.5 accent-primary"
                  />
                  <span className="text-2xs text-muted-foreground">No Expiry Date</span>
                </label>
              </div>
            </div>

            {/* Shelf life indicator */}
            {shelfPct !== null && (
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${shelfPct >= 80 ? 'bg-primary/5 border-primary/20' : shelfPct >= 50 ? 'bg-accent/5 border-accent/20' : 'bg-red-500/5 border-red-500/20'}`}>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Shelf Life Remaining</span>
                    <span className={`text-xs font-bold ${shelfPct >= 80 ? 'text-primary' : shelfPct >= 50 ? 'text-accent' : 'text-red-400'}`}>{shelfPct}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${shelfPct >= 80 ? 'bg-primary' : shelfPct >= 50 ? 'bg-accent' : 'bg-red-400'}`}
                      style={{ width: `${shelfPct}%` }}
                    />
                  </div>
                </div>
                {shelfPct < 80 && (
                  <AlertTriangle size={14} className={shelfPct < 50 ? 'text-red-400' : 'text-accent'} />
                )}
              </div>
            )}

            {/* Row 3: Qty + UOM + Rack */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  Quantity <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={(e) => setField('quantity')(e.target.value)}
                  placeholder="e.g. 100"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">UOM</label>
                <select
                  value={form.uom}
                  onChange={(e) => setField('uom')(e.target.value)}
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                >
                  {UOM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Layers size={12} /> Rack / Location
                </label>
                <input
                  type="text"
                  value={form.rack}
                  onChange={(e) => setField('rack')(e.target.value)}
                  placeholder="e.g. A-01-02-B-05"
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Remarks */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Remarks</label>
              <textarea
                value={form.remarks}
                onChange={(e) => setField('remarks')(e.target.value)}
                placeholder="Any notes about this item…"
                rows={2}
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all resize-none"
              />
            </div>

            {/* Add Item Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!isItemValid}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-all"
              >
                <Plus size={15} />
                Add Item to List
              </button>
            </div>
          </div>
        </div>

        {/* Items List */}
        {items.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-primary" />
                <h2 className="text-sm font-bold text-foreground">Step 3 — Review &amp; Submit for QC</h2>
                <span className="badge-pending text-2xs font-bold px-2 py-0.5 rounded-full">{items.length} item{items.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Item Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Batch</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Expiry</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Rack</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((item) => (
                    <tr key={item.id} className="row-hover">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-foreground">{item.itemName}</p>
                        <p className="text-2xs text-muted-foreground mt-0.5">{item.id}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-primary">{item.batchNumber}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.expiryDate || '—'}</td>
                      <td className="px-4 py-3 font-semibold text-foreground">{item.quantity} <span className="text-muted-foreground font-normal">{item.uom}</span></td>
                      <td className="px-4 py-3 font-mono text-muted-foreground">{item.rack || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="badge-pending text-2xs font-bold px-2 py-0.5 rounded-full">PENDING QC</span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-all"
                          title="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setItems([])}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground text-sm font-medium hover:text-foreground transition-all"
              >
                <RotateCcw size={13} />
                Clear All
              </button>
              <button
                type="button"
                onClick={handleSubmitForQC}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-all"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={15} />
                    Submit {items.length} Item{items.length !== 1 ? 's' : ''} for QC Review
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
